import type { ApiClient } from "../api/client";
import { ApiError, describeCode, isApiError } from "../api/errors";
import type { Accepted, CaptureInput, Command, CommandInput, CommandKind } from "../api/types";
import { announce } from "./announcer";
import { createStore } from "./store";
import type { PlaybackSync } from "./sync";

/**
 * 再生操作とここ好きの受付・結果の追跡。
 *
 * 202 は「受け付けた」だけ。成功・拒否・失敗・中断は command_id で後から確かめる（仕様書 /commands/{id}）。
 * - 画面は「送信中 → 受付済み（実行待ち）→ 完了／拒否／失敗／中断」を区別して出す
 * - 受付前に通信が切れたときだけ、同じ冪等IDで1回だけ送り直す（二重に追加されない）
 * - 一定時間たっても結果が分からないときは「結果を確認できない」と出し、成功とは言わない
 * - 再生状態は、結果が出たあとにスナップショットを取り直して反映する（先回りして書きかえない）
 */

export type CommandPhase = "sending" | "accepted" | "succeeded" | "rejected" | "failed" | "interrupted" | "send_failed" | "unknown";

export type TrackedKind = CommandKind | "capture";

export interface TrackedCommand {
  localId: string;
  kind: TrackedKind;
  /** 何の操作か（「スキップ」「『夜明けのプリズム』を追加」） */
  label: string;
  idempotencyKey: string;
  phase: CommandPhase;
  commandId: string | null;
  errorCode: string | null;
  resultId: string | null;
  startedAt: number;
  /** ここ好きを押した画面。遅延した結果で別画面を奪わないため */
  originKey?: string;
  /** 利用者に見せる文 */
  message: string;
}

const DONE_TEXT: Record<TrackedKind, string> = {
  play: "再生を始めました",
  enqueue: "キューに追加しました",
  pause: "一時停止しました",
  resume: "再開しました",
  skip: "スキップしました",
  stop: "再生を終了しました",
  remove: "キューから外しました",
  seek: "再生位置を移動しました",
  repeat: "リピートを切りかえました",
  dj_mix: "DJ Mixを切りかえました",
  mix_preference: "希望を保存しました。反映状況は希望の一覧で確認できます",
  mix_preference_clear: "希望を解除して、おまかせに戻しました",
  reorder: "並べ替えました",
  clear: "未再生のキューを空にしました",
  capture: "ここ好きを下書きに記録しました（自分だけに見えます）",
};

export const TERMINAL: CommandPhase[] = ["succeeded", "rejected", "failed", "interrupted", "send_failed", "unknown"];

export function isInFlight(c: TrackedCommand): boolean {
  return c.phase === "sending" || c.phase === "accepted";
}

function newKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export interface CommandTrackerOptions {
  pollMs?: number;
  /** これを過ぎても結果が分からなければ「確認できない」 */
  timeoutMs?: number;
  now?: () => number;
}

export class CommandTracker {
  readonly store = createStore<TrackedCommand[]>([]);
  private pollMs: number;
  private timeoutMs: number;
  private now: () => number;
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  private seq = 0;

  constructor(
    private api: ApiClient,
    private sync: PlaybackSync | null,
    opts: CommandTrackerOptions = {},
  ) {
    this.pollMs = opts.pollMs ?? 800;
    this.timeoutMs = opts.timeoutMs ?? 30_000;
    this.now = opts.now ?? Date.now;
    sync?.onEvent((e) => {
      if (e.resources.includes("commands")) this.pollAll();
    });
  }

  /** 再生操作を送る。input は idempotency_key 以外を渡す */
  sendCommand(label: string, input: Omit<CommandInput, "idempotency_key">): TrackedCommand {
    return this.track(input.kind, label, (key) => this.api.postCommand({ ...input, idempotency_key: key }));
  }

  /** ここ好き（サーバーが受付時点の位置で下書きを作る） */
  capture(label: string, input: Omit<CaptureInput, "idempotency_key">, originKey?: string): TrackedCommand {
    return this.track("capture", label, (key) => this.api.captureMoment({ ...input, idempotency_key: key }), originKey);
  }

  dismiss(localId: string) {
    this.clearTimer(localId);
    this.store.set((list) => list.filter((c) => c.localId !== localId));
  }

  clear() {
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
    this.store.set([]);
  }

  /** 同じ種類の操作が実行待ちか（ボタンを押せないようにする） */
  pending(kind: TrackedKind, label?: string): boolean {
    return this.store.get().some((c) => c.kind === kind && isInFlight(c) && (label === undefined || c.label === label));
  }

  private suffix() {
    return this.api.mode === "mock" ? "（モック）" : "";
  }

  private update(localId: string, patch: Partial<TrackedCommand>) {
    this.store.set((list) => list.map((c) => (c.localId === localId ? { ...c, ...patch } : c)));
  }

  private get(localId: string) {
    return this.store.get().find((c) => c.localId === localId);
  }

  private track(kind: TrackedKind, label: string, send: (key: string) => Promise<Accepted>, originKey?: string): TrackedCommand {
    const entry: TrackedCommand = {
      localId: `c${(this.seq += 1)}`,
      kind,
      label,
      idempotencyKey: newKey(),
      phase: "sending",
      commandId: null,
      errorCode: null,
      resultId: null,
      startedAt: this.now(),
      originKey,
      message: `${label}：送信中…`,
    };
    // 終わったものは5件まで残す
    this.store.set((list) => [entry, ...list.filter((c) => isInFlight(c)), ...list.filter((c) => !isInFlight(c)).slice(0, 4)]);
    void this.run(entry, send);
    return entry;
  }

  private async run(entry: TrackedCommand, send: (key: string) => Promise<Accepted>) {
    let accepted: Accepted | null = null;
    for (let attempt = 0; attempt < 2 && !accepted; attempt += 1) {
      try {
        accepted = await send(entry.idempotencyKey);
      } catch (e) {
        const retryable = isApiError(e) && e.status === 0 && e.code === "network" && attempt === 0;
        if (retryable) continue;
        this.failBeforeAccept(entry, e);
        return;
      }
    }
    if (!accepted || !this.get(entry.localId)) return;
    this.update(entry.localId, {
      phase: "accepted",
      commandId: accepted.command_id,
      message: `${entry.label}：受け付けました。Botの実行を待っています…`,
    });
    announce(`${entry.label}を受け付けました。実行を待っています。`);
    this.schedulePoll(entry.localId);
  }

  private failBeforeAccept(entry: TrackedCommand, e: unknown) {
    if (!this.get(entry.localId)) return;
    const err = isApiError(e) ? e : new ApiError(0, "network");
    let phase: CommandPhase = "failed";
    if (err.status === 0 && err.code === "network") phase = "send_failed";
    else if (err.status === 403 || err.status === 409 || err.status === 422) phase = "rejected";
    const message =
      phase === "send_failed"
        ? `${entry.label}：送信できませんでした。通信が戻ってから、もう一度お試しください（実行されていません）。`
        : `${entry.label}：${describeCode(err.code, err.status)}`;
    this.update(entry.localId, { phase, errorCode: err.code, message });
    announce(message, "assertive");
    if (err.status === 409) void this.sync?.refresh();
  }

  private schedulePoll(localId: string) {
    this.clearTimer(localId);
    this.timers.set(
      localId,
      setTimeout(() => void this.poll(localId), this.pollMs),
    );
  }

  private clearTimer(localId: string) {
    const t = this.timers.get(localId);
    if (t) clearTimeout(t);
    this.timers.delete(localId);
  }

  private pollAll() {
    this.store.get().forEach((c) => {
      if (c.phase === "accepted") void this.poll(c.localId);
    });
  }

  private async poll(localId: string) {
    const entry = this.get(localId);
    if (!entry || entry.phase !== "accepted" || !entry.commandId) return;
    let cmd: Command;
    try {
      cmd = await this.api.getCommand(entry.commandId);
    } catch {
      return this.keepWaiting(localId);
    }
    const latest = this.get(localId);
    if (!latest || latest.phase !== "accepted") return;
    if (cmd.status === "pending") return this.keepWaiting(localId);
    this.clearTimer(localId);
    this.finish(latest, cmd);
  }

  private keepWaiting(localId: string) {
    const entry = this.get(localId);
    if (!entry) return;
    if (this.now() - entry.startedAt > this.timeoutMs) {
      const message = `${entry.label}：受け付けましたが、結果を確認できませんでした。再生の状態を確かめてください。`;
      this.update(localId, { phase: "unknown", message });
      announce(message, "assertive");
      void this.sync?.refresh();
      return;
    }
    this.schedulePoll(localId);
  }

  private finish(entry: TrackedCommand, cmd: Command) {
    let message: string;
    switch (cmd.status) {
      case "succeeded":
        message = `${DONE_TEXT[entry.kind]}${this.suffix()}`;
        break;
      case "rejected":
        message = `${entry.label}：${describeCode(cmd.error_code)}`;
        break;
      case "interrupted":
        message = `${entry.label}：Botが止まったため中断しました。自動ではやり直しません。`;
        break;
      default:
        message = `${entry.label}：実行に失敗しました。${cmd.error_code ? describeCode(cmd.error_code) : ""}`.trim();
    }
    const phase = cmd.status as CommandPhase;
    this.update(entry.localId, { phase, errorCode: cmd.error_code, resultId: cmd.result_id, message });
    announce(message, phase === "succeeded" ? "polite" : "assertive");
    // 結果は、スナップショットを取り直して反映する
    void this.sync?.refresh();
  }
}
